const env = import.meta.env;
const isTest = env.MODE === "test";

function required(key: string, testPlaceholder: string): string {
  const value = env[key] as string | undefined;
  if (!value) {
    if (isTest) return testPlaceholder;
    throw new Error(`Missing required env var: ${key}`);
  }
  return value;
}

export const config = {
  apiUrl: (env.VITE_API_URL as string) || "http://localhost:3000",
  // Base URL for /auth/* endpoints. Defaults to apiUrl so prod/staging (where
  // the api has its own subdomain, e.g. api.prod.nexus.adge.ai) are unchanged.
  // PR envs share a hostname between app + api and the shared HTTPRoute strips
  // the /api prefix — that rewrite breaks Path=/auth cookies because the
  // browser stores the cookie against the ORIGINAL URL (/api/auth/...) and
  // won't send it back for future /auth/* requests. In PR envs we route
  // /auth/* directly to the api service without stripping, and point authUrl
  // at the app origin (no /api suffix) so the URLs the browser sees match
  // the cookie's Path attribute.
  authUrl: (env.VITE_AUTH_URL as string) || (env.VITE_API_URL as string) || "http://localhost:3000",
  // Base URL of services/mcp. Used only by the /auth/mcp-authorize consent
  // screen, which hands a Firebase ID token to the MCP OAuth server. MCP runs
  // on its own host (mcp.<env>.nexus.adge.ai) so it never shares an origin
  // with the API.
  mcpUrl: (env.VITE_MCP_URL as string) || "http://localhost:3001",
  isSandbox: env.VITE_NEXUS_USE_SANDBOX_ENV_VARS === "true",

  // True in `vite dev`, false in production builds (Vite static-replaces import.meta.env.DEV)
  isDev: env.DEV === true,

  // Environment (development | staging | production)
  environment: (env.VITE_ENVIRONMENT as string) || env.MODE as string,

  // Sentry
  sentryDsn: (env.VITE_SENTRY_DSN as string) || "",
  sentryRelease: (env.VITE_SENTRY_RELEASE as string) || "",
  // Sentry environment override. PR envs all report as ONE Sentry
  // environment ("pr-environments") and are told apart by the pr_id tag —
  // per-PR environments would explode Sentry's environment dropdown.
  sentryEnvironment:
    (env.VITE_SENTRY_ENVIRONMENT as string) || (env.VITE_ENVIRONMENT as string) || (env.MODE as string),
  // PR number tag for Sentry filtering in PR envs; empty elsewhere.
  prId: (env.VITE_PR_ID as string) || "",
  // Sentry organisation slug, used only to build a permalink to a stored trace
  // id. The DSN does not carry it, so without this the admin Events tab simply
  // shows no trace link rather than a broken one.
  sentryOrgSlug: (env.VITE_SENTRY_ORG_SLUG as string) || "",

  // Firebase
  firebaseApiKey: required("VITE_FIREBASE_API_KEY", "test-api-key"),
  firebaseProjectId: required("VITE_FIREBASE_PROJECT_ID", "test-project"),
  firebaseAuthDomain: required("VITE_FIREBASE_AUTH_DOMAIN", "test.firebaseapp.com"),

  // Amplitude
  amplitudeApiKey: (env.VITE_AMPLITUDE_API_KEY as string) || "",
  amplitudeDebug: env.VITE_AMPLITUDE_DEBUG === "true",

  // Deploy metadata (CI-injected, empty in local dev)
  deployBranch: (env.VITE_DEPLOY_BRANCH as string) || "",
  deployedAt: (env.VITE_DEPLOYED_AT as string) || "",
  deployedBy: (env.VITE_DEPLOYED_BY as string) || "",
  deploySha: (env.VITE_DEPLOY_SHA as string) || "",
} as const;
